import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, History,
  AlertCircle, CheckCircle2, Info,
} from "lucide-react";

function slugFromParam(p) {
  return (p || "").toUpperCase().replace("-", "_");
}

function computeTotal(questions, multiAll) {
  let t = 0;
  for (const q of questions || []) {
    const opts = q.options || [];
    if (!opts.length) continue;
    const scores = opts.map((o) => Number(o.score) || 0);
    t += multiAll || q.type === "multi" ? scores.reduce((a, b) => a + b, 0) : Math.max(...scores);
  }
  return Math.round(t * 1000) / 1000;
}

export default function FrameworkEditor() {
  const { slug: rawSlug } = useParams();
  const slug = slugFromParam(rawSlug);
  const navigate = useNavigate();

  const [def, setDef] = useState(null);  // active definition from API
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [changeNote, setChangeNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const [{ data: d }, { data: v }] = await Promise.all([
        api.get(`/admin/framework-editor/frameworks/${slug}`),
        api.get(`/admin/framework-editor/frameworks/${slug}/versions`),
      ]);
      setDef(d);
      setQuestions(d.questions || []);
      setCategories(d.categories || []);
      setVersions(v.items || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [slug]);

  const multiAll = def?.select_mode === "multi";
  const locked = !!def?.structure_locked;
  const total = useMemo(() => computeTotal(questions, multiAll), [questions, multiAll]);
  const maxScore = def?.max_score ?? null;
  const isValidTotal = maxScore == null || Math.abs(total - maxScore) < 0.01;

  const updateQuestion = (idx, patch) => {
    setQuestions((arr) => arr.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const updateOption = (qi, oi, patch) => {
    setQuestions((arr) => arr.map((q, i) => {
      if (i !== qi) return q;
      const opts = (q.options || []).map((o, j) => (j === oi ? { ...o, ...patch } : o));
      return { ...q, options: opts };
    }));
  };
  const addQuestion = () => {
    setQuestions((arr) => [...arr, {
      key: `q${Date.now().toString(36)}`,
      label: "New question",
      category: categories[0]?.id || null,
      type: multiAll ? "multi" : "single",
      options: [
        { id: "opt-0", label: "Option 1", score: 0 },
        { id: "opt-1", label: "Option 2", score: 1 },
      ],
    }]);
  };
  const removeQuestion = (idx) => {
    if (!confirm("Remove this question? Existing submissions remain linked to their stored version.")) return;
    setQuestions((arr) => arr.filter((_, i) => i !== idx));
  };
  const addOption = (qi) => {
    setQuestions((arr) => arr.map((q, i) => i === qi
      ? { ...q, options: [...(q.options || []), { id: `opt-${(q.options || []).length}`, label: "New option", score: 0 }] }
      : q));
  };
  const removeOption = (qi, oi) => {
    setQuestions((arr) => arr.map((q, i) => {
      if (i !== qi) return q;
      const opts = (q.options || []).filter((_, j) => j !== oi);
      return { ...q, options: opts };
    }));
  };

  const save = async () => {
    if (!isValidTotal) {
      toast.error(`Total must equal ${maxScore}. Currently ${total}.`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/framework-editor/frameworks/${slug}`, {
        questions, categories, change_note: changeNote,
      });
      toast.success(`Saved as v${data.version}`);
      setChangeNote("");
      await load();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "total_mismatch") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail) || ex.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !def) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading editor…</div>;

  return (
    <div className="space-y-6" data-testid="framework-editor-page">
      {/* Header */}
      <div className="border-b border-ink-line pb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/framework-editor" className="text-[11px] uppercase tracking-wider text-ink-muted hover:text-brand inline-flex items-center gap-1" data-testid="back-to-list">
            <ArrowLeft className="w-3 h-3" /> All frameworks
          </Link>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-3">
            {slug}
            <span className="chip border-ink-line bg-secondary text-ink-muted text-[10px] font-num">v{def.version} active</span>
          </h1>
          <div className="text-[13px] text-ink-muted mt-1">{def.name}</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="Change note (optional)"
            data-testid="change-note-input"
            className="px-3 py-2 border border-ink-line bg-white rounded-sm text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
          <button
            onClick={save}
            disabled={saving || !isValidTotal}
            data-testid="save-framework-btn"
            className={`px-4 py-2 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors ${
              !isValidTotal ? "bg-ink-line text-ink-muted cursor-not-allowed"
                : "bg-brand hover:bg-brand-hover text-white"
            }`}
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save as new version"}
          </button>
        </div>
      </div>

      {/* Total bar */}
      <div className={`card-flat p-4 flex items-center justify-between flex-wrap gap-3 ${isValidTotal ? "" : "border-warn"}`} data-testid="total-bar">
        <div className="flex items-center gap-4">
          <div>
            <div className="stat-label">Current Total</div>
            <div className={`font-num text-3xl font-bold tracking-tight ${isValidTotal ? "text-good" : "text-warn"}`} data-testid="total-value">
              {total}{maxScore != null && <span className="text-base text-ink-muted font-normal"> / {maxScore}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[12px] text-ink-muted">
            {isValidTotal ? (
              <><CheckCircle2 className="w-4 h-4 text-good" /> {multiAll ? "Sum of all option scores" : "Sum of max option scores per question"} equals the framework max.</>
            ) : (
              <><AlertCircle className="w-4 h-4 text-warn" /> Saving is blocked — total must equal {maxScore}.</>
            )}
          </div>
        </div>
        <div className="text-[11px] text-ink-muted uppercase tracking-wider flex items-center gap-2 font-num">
          <History className="w-3 h-3" /> {versions.length} version{versions.length === 1 ? "" : "s"} on file
          {!locked && (
            <button onClick={addQuestion} data-testid="add-question-btn" className="ml-3 inline-flex items-center gap-1 text-brand hover:text-brand-hover normal-case font-medium">
              <Plus className="w-3 h-3" /> Add question
            </button>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-3" data-testid="questions-list">
        {questions.map((q, qi) => (
          <div key={q.key + qi} className="card-flat p-5" data-testid={`question-card-${qi}`}>
            <div className="flex items-start gap-3">
              <GripVertical className="w-4 h-4 text-ink-muted mt-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-ink-muted font-num shrink-0">Q{qi + 1}</span>
                  {def.supports_category && (
                    <select
                      value={q.category || ""}
                      onChange={(e) => updateQuestion(qi, { category: e.target.value || null })}
                      data-testid={`question-category-${qi}`}
                      className="text-[12px] border border-ink-line rounded-sm px-2 py-1 bg-white"
                    >
                      <option value="">No category</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  )}
                  {(multiAll || q.type === "multi") && (
                    <span className="chip border-brand/30 bg-brand-soft text-brand text-[10px]" data-testid={`question-multi-${qi}`}>Multi-select · options add up</span>
                  )}
                  {!locked && (
                    <button onClick={() => removeQuestion(qi)} data-testid={`remove-question-${qi}`} className="ml-auto text-ink-muted hover:text-warn p-1" title="Remove question">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <textarea
                  value={q.label}
                  onChange={(e) => updateQuestion(qi, { label: e.target.value })}
                  data-testid={`question-text-${qi}`}
                  rows={2}
                  className="w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                />
                {q.prompt !== undefined && multiAll && (
                  <input
                    value={q.prompt || ""}
                    onChange={(e) => updateQuestion(qi, { prompt: e.target.value })}
                    placeholder="Helper text shown under the question (optional)"
                    data-testid={`question-prompt-${qi}`}
                    className="w-full mt-2 px-3 py-1.5 border border-ink-line bg-white rounded-sm text-[13px] text-ink-muted focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  />
                )}
                <div className="mt-3 space-y-1.5">
                  {(q.options || []).map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2" data-testid={`option-row-${qi}-${oi}`}>
                      <span className="text-[10px] uppercase tracking-wider text-ink-muted font-num w-6 text-right">{oi + 1}.</span>
                      <input
                        value={o.label}
                        onChange={(e) => updateOption(qi, oi, { label: e.target.value })}
                        data-testid={`option-label-${qi}-${oi}`}
                        className="flex-1 px-2 py-1.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                      <input
                        type="number"
                        step="0.1"
                        value={o.score}
                        onChange={(e) => updateOption(qi, oi, { score: parseFloat(e.target.value) || 0 })}
                        data-testid={`option-score-${qi}-${oi}`}
                        className="w-20 px-2 py-1.5 border border-ink-line bg-white rounded-sm text-sm text-right font-num focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                      {!locked && (
                      <button onClick={() => removeOption(qi, oi)} data-testid={`remove-option-${qi}-${oi}`} className="text-ink-muted hover:text-warn p-1" title="Remove option">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      )}
                    </div>
                  ))}
                  {!locked && (
                  <button onClick={() => addOption(qi)} data-testid={`add-option-${qi}`} className="text-[12px] text-brand hover:text-brand-hover inline-flex items-center gap-1 mt-1.5">
                    <Plus className="w-3 h-3" /> Add option
                  </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {locked && (
          <p className="text-[12px] text-ink-muted" data-testid="structure-locked-note">
            {slug}'s question order drives its reports, so questions can be edited but not added, removed or reordered.
          </p>
        )}
        {questions.length === 0 && (
          <div className="card-flat p-8 text-center text-ink-muted text-sm">
            No questions yet. <button onClick={addQuestion} className="text-brand underline">Add one</button>.
          </div>
        )}
      </div>

      {/* Version history */}
      {versions.length > 1 && (
        <div className="card-flat" data-testid="version-history">
          <div className="px-5 py-3 border-b border-ink-line">
            <div className="stat-label">Version History</div>
          </div>
          <ul className="divide-y divide-ink-line">
            {versions.map((v) => (
              <li key={v.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`version-row-${v.version}`}>
                <div>
                  <span className="font-num font-bold">v{v.version}</span>
                  {v.is_active && <span className="chip border-good/40 bg-good-soft text-good text-[10px] ml-2">ACTIVE</span>}
                  <span className="text-ink-muted ml-3 text-[12px]">{v.created_by_name || "—"}</span>
                  {v.change_note && <span className="text-ink-muted ml-3 text-[12px] italic">&ldquo;{v.change_note}&rdquo;</span>}
                </div>
                <span className="text-[11px] text-ink-muted font-num">{(v.created_at || "").slice(0, 19).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer note */}
      <div className="text-[12px] text-ink-muted flex items-start gap-2 pt-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Saving creates an immutable new version and applies it to the live assessment immediately.
          Scores already submitted are stored and never recalculated. New assessments use the
          active version going forward.
        </span>
      </div>
    </div>
  );
}
